import React, { useState } from 'react';
import Button from '../Button';
import { renameEnterpriseClass } from '../../api/admin';

/**
 * class-stream A4：单个活跃团队行。
 * 内联改名（编辑态切换）/ 转走成员（开 TransferMembersModal）/ 停用。
 *
 * Props:
 *   entId        企业 id
 *   cls          团队行 { id, name, join_code, member_count, instructor }
 *   onTransfer   点「转走成员」回调（父级开 TransferMembersModal）
 *   onDisable    点「停用」回调（父级执行 disable + 刷新）
 *   onRenamed    改名成功回调（父级刷新）
 *   onError      错误回调（父级用 Alert 展示）
 */
export default function ClassRowEditor({ entId, cls, onTransfer, onDisable, onRenamed, onError }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cls.name);
  const [saving, setSaving] = useState(false);

  const canDisable = cls.member_count === 0;

  const startEdit = () => { setName(cls.name); setEditing(true); };

  const save = async () => {
    if (!name.trim()) { onError?.('团队名称不能为空'); return; }
    if (name.trim() === cls.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await renameEnterpriseClass(entId, cls.id, { name: name.trim() });
      setEditing(false);
      onRenamed?.();
    } catch (e) {
      onError?.(e.response?.data?.detail || '改名失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={rowStyle}>
      <code style={codeStyle}>{cls.join_code}</code>

      {editing ? (
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={editInputStyle}
          autoFocus
        />
      ) : (
        <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{cls.name}</span>
      )}

      <span style={metaStyle}>成员 {cls.member_count}</span>
      {cls.instructor && <span style={metaStyle}>讲师 {cls.instructor.name}</span>}

      <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
        {editing ? (
          <>
            <Button size="sm" onClick={save} loading={saving}>保存</Button>
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>取消</Button>
          </>
        ) : (
          <>
            <button onClick={startEdit} style={actionBtn('#2563eb')}>改名</button>
            <button onClick={() => onTransfer?.(cls)} style={actionBtn('#7c3aed')}>转走成员</button>
            <button
              onClick={() => canDisable && onDisable?.(cls)}
              disabled={!canDisable}
              title={canDisable ? '' : '请先转走全部成员'}
              style={{ ...actionBtn('#d97706'), ...(canDisable ? {} : disabledBtn) }}
            >
              停用
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const rowStyle = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 14px', background: '#fff',
  border: '1px solid #e2e8f0', borderRadius: 10,
};
const codeStyle = { fontSize: 11, background: '#f1f5f9', padding: '2px 8px', borderRadius: 6, color: '#475569' };
const editInputStyle = { padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, flex: 1 };
const metaStyle = { fontSize: 12, color: '#94a3b8' };
const actionBtn = (color) => ({
  padding: '5px 12px', borderRadius: 8, border: `1px solid ${color}30`,
  background: `${color}0f`, color, fontSize: 12, cursor: 'pointer', fontWeight: 500,
});
const disabledBtn = { opacity: 0.45, cursor: 'not-allowed' };
